<template>
  <Apexchart
    width="100%"
    :height="height"
    type="area"
    :options="chartOptions"
    :series="series"
  />
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  id: { type: String, required: true },
  categories: { type: Array, required: true },
  seriesData: { type: Array, required: true },
  primaryColor: { type: String, default: "#2579EF" },
  height: { type: [Number, String], default: 300 },
});

const generateColors = (seriesData) => {
  const baseColors = [
    props.primaryColor,
    "#A146E8",
    "#48AF7D",
    "#D89435",
    "#AC531C",
    "#AC1C1E",
  ];

  return seriesData?.map((series, index) => {
    if (series.color) {
      return series.color;
    }
    return baseColors[index % baseColors.length];
  });
};

const generateCurveTypes = (count) => {
  const curveTypes = [];
  for (let i = 0; i < count; i++) {
    curveTypes.push(i % 2 === 0 ? "straight" : "smooth");
  }
  return curveTypes;
};

const getMaxValue = () => {
  if (!props.seriesData?.length) return 5;

  let max = 0;
  props.seriesData?.forEach((series) => {
    if (series?.data?.length > 0) {
      const seriesMax = Math.max(...series.data);
      if (seriesMax > max) max = seriesMax;
    }
  });

  return max === 0 ? 5 : max;
};

const chartOptions = computed(() => {
  const maxValue = getMaxValue();
  const tickAmount = Math.min(5, maxValue);

  return {
    chart: {
      id: props.id,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    xaxis: {
      categories: props.categories,
      tickPlacement: "on",
      labels: {
        style: {
          fontSize: "11px",
          colors: "#888",
          textAlign: "center",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: 400,
          lineHeight: "normal",
        },
      },
      axisBorder: {
        show: true,
        color: "var(--white-opacity-8)",
        width: "100%",
      },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "11px",
          colors: "#888",
          textAlign: "center",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: 400,
          lineHeight: "normal",
        },
        formatter: (value) => Math.round(value),
      },
      min: 0,
      max: maxValue,
      tickAmount: tickAmount,
      forceNiceScale: true,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0.1,
        inverseColors: true,
        opacityFrom: 0.5,
        opacityTo: 0.2,
        stops: [0, 90],
      },
    },
    colors: generateColors(props.seriesData),
    stroke: {
      width: 3,
      curve: generateCurveTypes(props.seriesData?.length),
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      show: true,
      position: "back",
      borderColor: "var(--white-opacity-8)",
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "left",
      fontSize: "12px",
      fontFamily: "Proxima Nova, sans-serif",
      fontWeight: 400,
      colors: "#888",
      markers: {
        size: 5,
        shape: "circle",
        strokeWidth: 0,
        offsetX: -4,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      labels: {
        colors: "#888",
        useSeriesColors: false,
      },
      offsetX: -16,
      offsetY: 4,
    },
    tooltip: {
      theme: "dark",
      shared: true,
      intersect: false,
      y: {
        formatter: (value) =>
          Number.isInteger(value) ? value : value.toFixed(2),
      },
    },
  };
});

const series = computed(
  () =>
    props.seriesData?.map((series) => ({
      name: series?.name,
      data: series?.data || [],
    })) || [],
);
</script>
